import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import * as echarts from "echarts";
import { colors } from "../../../utils/colors";

export interface GenericBarChartRow {
  label: string;
  value: number;
  color?: string;
}

export interface GenericBarChartProps {
  title?: string;
  titleAlign?: "left" | "center" | "right";
  data: GenericBarChartRow[];
  seriesName?: string;
  loading?: boolean;
  height?: number;
  minHeight?: number;
  emptyMessage?: string;
  showLegend?: boolean;
  legendTop?: number;
  legendRight?: number;
  barWidth?: string;
  xAxisLabelColor?: string;
  xAxisLabelFontSize?: string;
  xAxisLabelFontWeight?: number;
  yAxisLabelColor?: string;
  yAxisLabelFontSize?: string;
  xAxisRotateThreshold?: number;
  tooltipValueFormatter?: (value: number) => string;
  axisTooltipFormatter?: (ctx: {
    dataIndex: number;
    categoryLabel: string;
    primaryValue: number;
    seriesMarkerHtml: string;
  }) => string;
  yAxisValueFormatter?: (value: number) => string;
  resizeTrigger?: string | number | boolean | null;
  defaultBarColor?: string;
}

export const GenericBarChart: React.FC<GenericBarChartProps> = ({
  title,
  titleAlign = "left",
  data,
  seriesName = "Series",
  loading = false,
  height = 400,
  minHeight = 400,
  emptyMessage = "No data available for the selected filters.",
  showLegend = true,
  legendTop = 8,
  legendRight = 50,
  barWidth = "35%",
  xAxisLabelColor = "#000000",
  xAxisLabelFontSize = "0.625rem",
  xAxisLabelFontWeight = 500,
  yAxisLabelColor = "#666666",
  yAxisLabelFontSize = "0.6875rem",
  xAxisRotateThreshold = 8,
  tooltipValueFormatter,
  axisTooltipFormatter,
  yAxisValueFormatter,
  resizeTrigger,
  defaultBarColor = "#4FC3F7",
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const hasData = data.length > 0;

  const scheduleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      cancelAnimationFrame(resizeRafRef.current);
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      chartInstance.current?.resize();
    });
  }, []);

  const defaultTooltipValueFormatter = useCallback(
    (v: number) =>
      Number(v).toLocaleString("en-US", {
        maximumFractionDigits: 2,
      }),
    [],
  );

  const option = useMemo(() => {
    const formatTooltipValue =
      tooltipValueFormatter ?? defaultTooltipValueFormatter;

    const tooltipOutsideChart = {
      appendToBody: true,
      confine: false,
      extraCssText: "z-index: 10000;",
      textStyle: {
        color: colors.chart_text,
      },
    };

    const tooltip =
      axisTooltipFormatter === undefined
        ? {
            trigger: "axis" as const,
            axisPointer: { type: "shadow" as const },
            valueFormatter: formatTooltipValue,
            ...tooltipOutsideChart,
          }
        : {
            trigger: "axis" as const,
            axisPointer: { type: "shadow" as const },
            renderMode: "html" as const,
            ...tooltipOutsideChart,
            formatter: (rawParams: unknown) => {
              const first = (
                Array.isArray(rawParams) ? rawParams[0] : rawParams
              ) as { dataIndex?: number; marker?: string } | undefined;
              const idx = first?.dataIndex ?? 0;
              const row = data[idx];
              if (!row) return "";
              const markerFromSeries = first?.marker?.trim();
              const barColor = row.color ?? defaultBarColor;
              const seriesMarkerHtml =
                markerFromSeries && markerFromSeries.length > 0
                  ? markerFromSeries
                  : `<span style="display:inline-block;margin-right:5px;width:10px;height:10px;border-radius:2px;background-color:${barColor};vertical-align:middle;"></span>`;
              return axisTooltipFormatter({
                dataIndex: idx,
                categoryLabel: row.label,
                primaryValue: row.value,
                seriesMarkerHtml,
              });
            },
          };

    return {
      tooltip,
      legend: {
        show: showLegend,
        data: [seriesName],
        top: legendTop,
        right: legendRight,
        orient: "horizontal",
        icon: "roundRect",
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 16,
      },
      grid: {
        left: "5%",
        right: "5%",
        top: showLegend ? "14%" : "8%",
        bottom: "18%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: data.map((r) => r.label),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: xAxisLabelFontSize,
          color: xAxisLabelColor,
          rotate: data.length > xAxisRotateThreshold ? 35 : 0,
          fontWeight: xAxisLabelFontWeight,
        },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: { color: yAxisLabelColor, opacity: 0.2 },
        },
        axisLabel: {
          fontSize: yAxisLabelFontSize,
          color: yAxisLabelColor,
          formatter:
            yAxisValueFormatter ??
            ((val: number) => Number(val).toLocaleString("en-US")),
        },
      },
      series: [
        {
          name: seriesName,
          type: "bar",
          color: defaultBarColor,
          itemStyle: {
            color: defaultBarColor,
          },
          data: data.map((item) => ({
            value: item.value,
            itemStyle: {
              color: item.color ?? defaultBarColor,
              borderRadius: [2, 2, 0, 0],
            },
          })),
          barWidth,
        },
      ],
    };
  }, [
    axisTooltipFormatter,
    barWidth,
    data,
    defaultBarColor,
    defaultTooltipValueFormatter,
    legendRight,
    legendTop,
    seriesName,
    showLegend,
    tooltipValueFormatter,
    xAxisLabelColor,
    xAxisLabelFontSize,
    xAxisLabelFontWeight,
    xAxisRotateThreshold,
    yAxisLabelColor,
    yAxisLabelFontSize,
    yAxisValueFormatter,
  ]);

  useEffect(() => {
    if (!hasData) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      chartInstance.current?.dispose();
      chartInstance.current = null;
      return undefined;
    }

    const dom = chartRef.current;
    if (!dom) {
      return undefined;
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(dom);
      const ro = new ResizeObserver(() => {
        scheduleResize();
      });
      ro.observe(dom);
      resizeObserverRef.current = ro;
      globalThis.addEventListener("resize", scheduleResize);
    }

    return () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      globalThis.removeEventListener("resize", scheduleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [hasData, scheduleResize]);

  useEffect(() => {
    if (!hasData || !chartInstance.current) {
      return;
    }
    chartInstance.current.setOption(option, {
      notMerge: true,
      lazyUpdate: true,
    });
    scheduleResize();
  }, [hasData, option, scheduleResize]);

  useEffect(() => {
    if (resizeTrigger === undefined) return;
    const timer = globalThis.setTimeout(() => scheduleResize(), 350);
    return () => globalThis.clearTimeout(timer);
  }, [resizeTrigger, scheduleResize]);

  if (!hasData && !loading) {
    return (
      <Box>
        {title ? (
          <Typography
            sx={{
              px: 2,
              mb: "0.25rem",
              fontWeight: 500,
              fontSize: "16px",
              color: colors.light_black,
              textAlign: titleAlign,
            }}
          >
            {title}
          </Typography>
        ) : null}
        <Box
          sx={{
            minWidth: 0,
            px: 2,
            py: 6,
            minHeight: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {title ? (
        <Typography
          sx={{
            px: 2,
            mb: "0.25rem",
            fontWeight: 500,
            fontSize: "16px",
            color: colors.light_black,
            textAlign: titleAlign,
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{
          minHeight,
          px: 2,
          opacity: loading && !hasData ? 0.5 : 1,
          minWidth: 0,
        }}
      >
        {hasData ? (
          <Box ref={chartRef} sx={{ width: "100%", minWidth: 0, height }} />
        ) : (
          <Box sx={{ width: "100%", minHeight, minWidth: 0 }} />
        )}
      </Box>
    </Box>
  );
};
